import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Barnsley\'s AMBER DfT rating mean I cannot claim?',
    answer: 'No. AMBER means Barnsley performs adequately in some areas and inadequately in others across condition, spend and best practice — but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. The council still filled 26,483 potholes in 2023/24, B/C road green condition fell from 72.42% to 67.72% between 2020 and 2024, and unclassified roads reached 21.85% RED condition in 2022 before emergency intervention.',
  },
  {
    question: 'Why did Barnsley potholes surge 48% despite 93% preventative spending?',
    answer: 'The council\'s transparency report shows capital spend of £11m–£14m annually with 93–95% directed to preventative treatments, yet estimated pothole fills jumped from 17,898 (2022/23) to 26,483 (2023/24) — a 48% rise and the highest figure in the published five-year series. That pattern suggests defects still formed faster than preventative resurfacing could stabilise the network, particularly after post-winter deterioration programmes like the emerging priority patching scheme launched in 2023/24.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 727km — 67% of Barnsley\'s 1,090km carriageway network. The council publishes RED-condition percentages for U-roads only (not full red/amber/green breakdowns), assessed via coarse visual inspection of 100% of the network each year. U-road RED condition peaked at 21.85% in 2022 before falling to 10.78% by 2024 after targeted capital investment — but that crisis level developed on the roads where most pothole damage occurs.',
  },
  {
    question: 'Barnsley has a GREEN spend rating — can I still claim?',
    answer: 'Yes. The DfT Spend scorecard is GREEN because projected 2025/26 capital spend (£11.122m) nearly doubles the DfT allocation (£5.999m). Section 58 turns on whether the specific defect was reasonably inspected and repaired — not on aggregate budgets. The overall and condition ratings are both AMBER, and high spend alongside a 48% pothole surge in 2023/24 shows investment volume alone does not prevent reactive failure on individual roads.',
  },
  {
    question: 'Are Barnsley\'s B and C roads improving or deteriorating?',
    answer: 'Deteriorating on published SCANNER data. B/C road green condition fell from 72.42% (2020) to 67.72% (2024), while RED condition rose from 3.75% to 5.56%. The council\'s 217km of B/C roads are surveyed via SCANNER over a two-year cycle — if your pothole was on a classified link road, this five-year decline is directly citable from the transparency report.',
  },
  {
    question: 'Public pothole reports more than doubled — what does that mean for Section 58?',
    answer: 'Public reports rose from 3,878 (16.2% of repairs, 2020/21) to 8,438 (31.9%, 2023/24) before falling to 4,672 (22.9%, 2024/25). That peak coincided with the U-road RED crisis and the record pothole repair year. If you reported the defect before your incident — or Fixtyer finds prior reports — that is evidence of actual notice beyond statutory inspection cycles.',
  },
  {
    question: 'How do I report a pothole to Barnsley Metropolitan Borough Council?',
    answer: 'Report potholes online at barnsley.gov.uk via the council\'s report-a-pothole service, or call the 24-hour emergency helpline on 01226 773555 if the defect poses immediate danger. Prior reports strengthen a claim by demonstrating the council had notice. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BarnsleyCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Barnsley Pothole Claims | 26,483 Repairs in 2023/24 — 48% Surge | Fixtyer"
        description="Barnsley earns AMBER DfT condition ratings but filled 26,483 potholes in 2023/24 — up 48%. U-roads hit 21.85% RED in 2022. See what that means for your Section 58 claim."
        canonical="/councils/barnsley"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Barnsley', url: '/councils/barnsley' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Barnsley Metropolitan Borough Council"
        councilSlug="barnsley"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/barnsley"
        headline="Barnsley Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Barnsley</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            26,483 Pothole Repairs in 2023/24 — Up 48% Despite 93% Preventative Spend
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Barnsley Metropolitan Borough Council earns <strong className="text-amber-400">AMBER</strong> DfT scorecards
            for overall performance and condition, with <strong className="text-emerald-400">GREEN</strong> spend — yet
            its own transparency report records <strong className="text-rose-400">26,483 pothole fills in 2023/24</strong>,
            a 48% surge from 17,898 the previous year. Sixty-seven per cent of the network is unclassified residential
            roads where RED condition hit <strong className="text-rose-400">21.85% in 2022</strong>. Section 58 still
            turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">+48%</div>
            <div className="text-white font-medium mt-2">Pothole surge in one year</div>
            <div className="text-brand-300 text-sm mt-1">
              From 17,898 repairs (2022/23) to 26,483 (2023/24) — the highest in the council's published five-year
              series, despite 93% preventative capital spend in the same period.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Department for Transport Scorecards
          </h2>
          <p className="text-surface-600 mb-8">
            Barnsley's 2025/26 ratings from the DfT local road maintenance programme
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full">
              <caption className="sr-only">Barnsley DfT road maintenance scorecards 2025/26</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Scorecard</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">What it reflects</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                  <td className="px-6 py-4 text-surface-600 text-sm">Mixed performance across condition, spend and best practice</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                  <td className="px-6 py-4 text-surface-600 text-sm">Network condition adequate in some areas, inadequate in others</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                  <td className="px-6 py-4 text-surface-600 text-sm">Projected capital spend (£11.122m) nearly doubles DfT allocation (£5.999m)</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Best Practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                  <td className="px-6 py-4 text-surface-600 text-sm">Maintenance approaches adequate in some areas, room for improvement elsewhere</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-4">
            <p className="text-amber-800 text-sm">
              <strong>What AMBER overall means:</strong> Barnsley invests substantially — a £17.214m highways capital
              programme is planned for 2025/26 — but the DfT still rates condition and best practice AMBER. That
              combination is consistent with a council spending heavily while published condition data shows B/C roads
              deteriorating and pothole repair volumes spiking.
            </p>
          </div>

          <p className="text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
          </p>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            1,090km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Barnsley's 2025 transparency report — a £2.1 billion asset under strain
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,090km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
              <div className="text-xs text-surface-500 mt-1">£2.1bn estimated replacement value</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">727km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">67% of the network</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">217km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">145km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Barnsley highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways and shared cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">1,430km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Other public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">725km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway gullies</td>
                  <td className="px-6 py-4 text-center font-medium">~55,000</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway structures</td>
                  <td className="px-6 py-4 text-center font-medium">225 (bridges, culverts, footbridges, subways)</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Street lights</td>
                  <td className="px-6 py-4 text-center font-medium">33,199</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="The estimated replacement cost of the highway network is estimated to be £2.1 billion and represents the biggest financial asset that we're responsible for."
            source="Barnsley Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What AMBER Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER surveys on classified roads; coarse visual inspection on 100% of U-roads annually
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Reporting caveat:</strong> Barnsley publishes full red/amber/green breakdowns for A and B/C roads
              via SCANNER surveys (100% coverage over a two-year cycle). For unclassified roads, only the percentage
              in RED condition is published — assessed via coarse visual inspection (CVI) on foot across all 727km each
              year. From 2026/27, PAS 2161 will introduce a five-category national standard; today's U-road data is
              RED-only.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (145km) — SCANNER surveys, two-year cycle</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.29%</td>
                    <td className="px-4 py-2 text-center">14.79%</td>
                    <td className="px-4 py-2 text-center">82.92%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.85%</td>
                    <td className="px-4 py-2 text-center">12.24%</td>
                    <td className="px-4 py-2 text-center">85.92%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">2.96%</td>
                    <td className="px-4 py-2 text-center font-bold">14.91%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">82.13%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition has risen from a low of 1.85% (2022) to 2.96% (2024). Principal routes remain
              predominantly green-rated but are only 13% of the carriageway network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">B and C roads (217km) — deteriorating steadily</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3.75%</td>
                    <td className="px-4 py-2 text-center">24.84%</td>
                    <td className="px-4 py-2 text-center">72.42%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3.63%</td>
                    <td className="px-4 py-2 text-center">23.87%</td>
                    <td className="px-4 py-2 text-center">72.49%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.56%</td>
                    <td className="px-4 py-2 text-center font-bold">26.73%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">67.72%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              B/C green condition fell 4.7 percentage points from 72.42% (2020) to 67.72% (2024), while RED rose from
              3.75% to 5.56% — deterioration despite consistent preventative capital spend of 93%+ annually.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (727km) — RED condition only published</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red (% requiring maintenance)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">13.36%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">15.13%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">21.85%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">15.60%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">10.78%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              More than one-fifth of residential roads needed maintenance in 2022 — roughly 159km of the U-road network.
              Targeted capital investment reduced RED to 10.78% by 2024, but that crisis developed on the roads where
              most pothole claims originate.
            </p>
          </div>

          <QuoteCard
            quote="Over this period, the targeted capital investment applied through sound asset management principles has seen the percentage of our unclassified road network requiring maintenance reduce from 21.85% in 2022 to 10.78% in 2024."
            source="Barnsley Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend — but reactive pothole workload remains substantial
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Barnsley highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">5,999</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">11,122</td>
                  <td className="px-4 py-3 text-center">806</td>
                  <td className="px-4 py-3 text-center text-emerald-400">93%</td>
                  <td className="px-4 py-3 text-center">7%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">4,592</td>
                  <td className="px-4 py-3 text-center">11,263</td>
                  <td className="px-4 py-3 text-center">781</td>
                  <td className="px-4 py-3 text-center">94%</td>
                  <td className="px-4 py-3 text-center">6%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">5,435</td>
                  <td className="px-4 py-3 text-center font-bold">13,805</td>
                  <td className="px-4 py-3 text-center">962</td>
                  <td className="px-4 py-3 text-center">93%</td>
                  <td className="px-4 py-3 text-center">7%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">4,000</td>
                  <td className="px-4 py-3 text-center">11,058</td>
                  <td className="px-4 py-3 text-center">892</td>
                  <td className="px-4 py-3 text-center">93%</td>
                  <td className="px-4 py-3 text-center">7%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">4,739</td>
                  <td className="px-4 py-3 text-center">10,323</td>
                  <td className="px-4 py-3 text-center">741</td>
                  <td className="px-4 py-3 text-center">93%</td>
                  <td className="px-4 py-3 text-center">7%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">8,696</td>
                  <td className="px-4 py-3 text-center">11,165</td>
                  <td className="px-4 py-3 text-center">604</td>
                  <td className="px-4 py-3 text-center">95%</td>
                  <td className="px-4 py-3 text-center">5%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Barnsley commits £3.3m of its own capital annually plus DfT grant, with an additional £2m per year
                since 2018/19 for backlog reduction. Projected 2025/26 capital spend of £11.122m against a £5.999m DfT
                allocation — within a £17.214m wider highways capital programme — exceeds what the DfT scorecard requires.
              </p>
            </div>
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Despite 93%+ preventative share, the council still filled 20,000–26,000 potholes annually across the
                published series. Revenue budgets fund reactive pothole repairs — and the 2023/24 peak of 26,483 repairs
                coincided with the highest capital spend (£13.805m) in the same period. Aggregate investment does not
                prove every defect was caught within inspection intervals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Volatile Pothole Repairs
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — from Barnsley's transparency report, split by discovery method
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Barnsley estimated potholes filled 2020-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Public reports</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Statutory inspections</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Total filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">3,878 (16.2%)</td>
                  <td className="px-6 py-4 text-center">20,068 (83.8%)</td>
                  <td className="px-6 py-4 text-center">23,946</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">5,308 (24.2%)</td>
                  <td className="px-6 py-4 text-center">16,688 (75.8%)</td>
                  <td className="px-6 py-4 text-center">21,996</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−8.1%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">5,685 (31.8%)</td>
                  <td className="px-6 py-4 text-center">12,213 (68.2%)</td>
                  <td className="px-6 py-4 text-center">17,898</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−25.3%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8,438 (31.9%)</td>
                  <td className="px-6 py-4 text-center">18,045 (68.1%)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26,483</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+48%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">4,672 (22.9%)</td>
                  <td className="px-6 py-4 text-center">15,753 (77.1%)</td>
                  <td className="px-6 py-4 text-center">20,425</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−22.9%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">118% rise in public reports at peak</h3>
              <p className="text-rose-800 text-sm">
                Public reports surged from 3,878 (2020/21) to 8,438 (2023/24) — drivers finding defects before
                statutory inspections could. That peak aligned with U-road RED condition at 21.85% (2022) and the
                record repair year.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Roughly 72 patches per day at peak</h3>
              <p className="text-amber-800 text-sm">
                26,483 pothole fills in 2023/24 works out to approximately 72 repairs per day across 1,090km. A
                network producing that reactive volume — after declining to 17,898 the previous year — is one where
                individual defects routinely form between inspection and resurfacing cycles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Barnsley says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> SCANNER laser surveys — 100% coverage over a two-year cycle</li>
                <li>• <strong>B/C roads:</strong> SCANNER surveys — 100% coverage over a two-year cycle</li>
                <li>• <strong>U roads:</strong> coarse visual inspection (CVI) on foot — 100% of 727km annually</li>
                <li>• <strong>Structures:</strong> 225 highway structures including bridges and culverts</li>
                <li>• <strong>From 2026/27:</strong> PAS 2161 five-category standard replaces current RAG methodology</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Reactive maintenance</h3>
              <p className="text-surface-600 text-sm mb-3">
                Reactive maintenance from revenue budgets focuses primarily on pothole repairs. The council uses
                water-activated cold-lay asphalt for urgent repairs and operates a risk-based approach to defect response.
              </p>
              <p className="text-surface-600 text-sm">
                The principal roads and side streets investment programme launched in 2023/24, followed by the emerging
                priority patching scheme addressing post-winter deterioration — both responses to defects forming faster
                than planned works could prevent.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Barnsley must maintain
              public highways. To defend a claim under <strong className="text-white">Section 58</strong>, it must show a
              reasonable system for inspecting and repairing the specific defect — not just publish a GREEN spend
              scorecard or cite £17m capital programmes.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the annual CVI cycle for U-roads, or the two-year SCANNER cycle for classified roads?</li>
              <li>• Did the defect meet intervention criteria during statutory inspections?</li>
              <li>• Were there prior public reports (council portal, FixMyStreet) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="Reactive maintenance, sourced from revenue budgets, focuses primarily on pothole repairs, which are critical to preserving road safety and structural integrity."
            source="Barnsley Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* 2025/26 plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            What Barnsley says it will deliver this financial year
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">£17.2m</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Highways capital programme</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">56</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Local road carriageway locations</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">32</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Principal roads and side streets</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">£2m</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Dedicated U-road investment</div>
              <div className="text-xs text-amber-700 mt-1">30–40 locations targeted</div>
            </div>
          </div>

          <QuoteCard
            quote="In 2025/26, Barnsley is delivering a £17.214 million capital programme, supported by council capital funding, government grant, and one-off investments."
            source="Barnsley Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
          />

          <div className="mt-6 bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              Even with 56 carriageway locations plus 32 principal-roads-and-side-streets sites, planned works touch a
              fraction of 1,090km. The remaining network relies on reactive patching, CVI/SCANNER survey cycles and
              statutory inspections — on a borough where U-road RED condition reached 21.85% within the last survey
              series and B/C green ratings fell 4.7 points in five years.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER-Rated Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Barnsley invests heavily — but published data shows deterioration and reactive spikes
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What works in the council's favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend nearly doubles DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 93–95% preventative capital share sustained over six years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED condition recovered from 21.85% (2022) to 10.78% (2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £17.214m highways capital programme and documented asset management strategy
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% annual CVI coverage of U-roads — not a blind spot like visual-only authorities
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-resourced Section 58 defence citing spend volumes and U-road recovery data.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall, condition and best-practice scorecards from DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 48% pothole surge to 26,483 in 2023/24 despite peak capital spend
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C green condition fell from 72.42% to 67.72% (2020–2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-roads reached 21.85% RED before emergency intervention
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Public reports more than doubled at peak (3,878 → 8,438)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road condition published as RED-only — limited transparency on amber/green residential network
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN spend and a £17m capital programme, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond inspection cycles</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — U-road crisis data (21.85% RED in 2022) or B/C deterioration trends as context</li>
              <li>• Timing relative to the 2023/24 post-winter patching surge and record repair year</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Barnsley's own
              transparency data where it helps you — without overstating what the council's recovery programmes prove
              about your specific road.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Barnsley Metropolitan Borough Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when
            you reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.barnsley.gov.uk/services/roads-travel-and-parking/roadworks-and-road-maintenance/report-a-pothole/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — barnsley.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Immediate danger: call the 24-hour emergency helpline on <strong>01226 773555</strong>
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Barnsley?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            Heavy investment demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 48% pothole surge cited</li>
                <li>• No prior-report search</li>
                <li>• No B/C deterioration or U-road crisis data</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 26,483 pothole repairs in 2023/24 documented</li>
                <li>✅ B/C road deterioration from council SCANNER data</li>
                <li>✅ U-road 21.85% RED crisis context included</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Barnsley</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=barnsley"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Barnsley Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.barnsley.gov.uk/services/roads-travel-and-parking/local-highway-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Barnsley Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
